import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import DashboardLayout from './components/Layout/DashboardLayout';
import Dashboard from './components/Dashboard/Dashboard';
import DashboardDebug from './components/Dashboard/DashboardDebug';
import Campaigns from './components/Campaigns/Campaigns';
import CreateCampaign from './components/Campaigns/CreateCampaign';
import UserCreateCampaign from './components/Campaigns/UserCreateCampaign';
import EditCampaign from './components/Campaigns/EditCampaign';
import CampaignDetail from './components/Campaigns/CampaignDetail';
import UserManagement from './components/Users/UserManagement';
import UserProfile from './components/Profile/UserProfile';
import LoadingSpinner from './components/Common/LoadingSpinner';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
    const { isAuthenticated, isLoading, isAdmin } = useAuth();

    if (isLoading) {
        return <LoadingSpinner />;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (adminOnly && !isAdmin()) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// User Only Route Component (for normal users)
const UserOnlyRoute = ({ children }) => {
    const { isAuthenticated, isLoading, isUser } = useAuth();

    if (isLoading) {
        return <LoadingSpinner />;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (!isUser()) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// Standalone Debug Layout Component
const StandaloneDebugLayout = () => {
    return (
        <div className="min-h-screen bg-gray-50">
            {/* Simple header */}
            <header className="bg-white shadow-sm border-b border-gray-200 mb-6">
                <div className="px-6 py-4">
                    <h1 className="text-2xl font-bold text-gray-900">🐛 Debug Dashboard</h1>
                    <p className="text-gray-600">Standalone debug version - no authentication required</p>
                </div>
            </header>
            <DashboardDebug />
        </div>
    );
};

// App Routes Component
const AppRoutes = () => {
    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/debug-standalone" element={<StandaloneDebugLayout />} />
            <Route
                path="/"
                element={
                    <ProtectedRoute>
                        <DashboardLayout />
                    </ProtectedRoute>
                }
            >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="debug" element={<DashboardDebug />} />
                <Route path="campaigns" element={<Campaigns />} />
                <Route path="campaigns/:id" element={<CampaignDetail />} />
                <Route
                    path="campaigns/new"
                    element={
                        <ProtectedRoute adminOnly>
                            <CreateCampaign />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="campaigns/create"
                    element={
                        <ProtectedRoute>
                            <UserCreateCampaign />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="campaigns/:id/edit"
                    element={
                        <ProtectedRoute>
                            <EditCampaign />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="users"
                    element={
                        <ProtectedRoute adminOnly>
                            <UserManagement />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="profile"
                    element={
                        <ProtectedRoute>
                            <UserProfile />
                        </ProtectedRoute>
                    }
                />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
        </Routes>
    );
};

function App() {
    return (
        <AuthProvider>
            <Router>
                <div className="App">
                    <AppRoutes />
                    <ToastContainer
                        position="top-right"
                        autoClose={5000}
                        hideProgressBar={false}
                        newestOnTop={true}
                        closeOnClick
                        rtl={false}
                        pauseOnFocusLoss
                        draggable
                        pauseOnHover
                        theme="light"
                        toastClassName="toast-message"
                        style={{ zIndex: 9999 }}
                        limit={3}
                    />
                </div>
            </Router>
        </AuthProvider>
    );
}

export default App; 